// src/pages/proyectos/components/SprintsList.tsx
import { useState } from 'react';
import { Button, Badge, Spinner } from '@/components/ui';
import { Plus, Calendar, Clock, Play, CheckCircle, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useSprints, useEliminarSprint, useActivarSprint, useCompletarSprint } from '@/hooks/queries/useProyectos';
import { SprintFormModal } from './SprintFormModal';
import { SPRINT_STATUS_LABELS, SPRINT_STATUS_COLORS } from '@/types';
import type { ProyectoSprint } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';

interface Props {
  proyectoId: string;
}

export function SprintsList({ proyectoId }: Props) {
  const { data: sprints, isLoading } = useSprints(proyectoId);
  const eliminarMutation = useEliminarSprint();
  const activarMutation = useActivarSprint();
  const completarMutation = useCompletarSprint();
  const [modalOpen, setModalOpen] = useState(false);
  const [sprintToEdit, setSprintToEdit] = useState<ProyectoSprint | undefined>();

  const handleEdit = (sprint: ProyectoSprint) => {
    setSprintToEdit(sprint);
    setModalOpen(true);
  };

  const handleDelete = async (sprint: ProyectoSprint) => {
    if (confirm(`¿Eliminar el sprint "${sprint.nombre}"?`)) {
      await eliminarMutation.mutateAsync({ sprintId: sprint.id, proyectoId });
    }
  };

  const handleActivar = async (sprint: ProyectoSprint) => {
    await activarMutation.mutateAsync({ sprintId: sprint.id, proyectoId });
  };

  const handleCompletar = async (sprint: ProyectoSprint) => {
    await completarMutation.mutateAsync({ sprintId: sprint.id, proyectoId });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSprintToEdit(undefined);
  };

  const getSprintProgress = (sprint: ProyectoSprint) => {
    const start = parseISO(sprint.fecha_inicio);
    const end = parseISO(sprint.fecha_fin);
    const now = new Date();

    if (now < start) return 0;
    if (now > end) return 100;

    const total = differenceInDays(end, start);
    const elapsed = differenceInDays(now, start);
    return Math.round((elapsed / total) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Sprints del proyecto</h3>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nuevo Sprint
        </Button>
      </div>

      {!sprints || sprints.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No hay sprints definidos. Crea el primer sprint del proyecto.
        </div>
      ) : (
        <div className="space-y-3">
          {sprints.map((sprint) => {
            const timeProgress = getSprintProgress(sprint);
            const isActive = sprint.estado === 'ACTIVO';

            return (
              <div
                key={sprint.id}
                className={`p-4 rounded-lg border transition-colors ${
                  isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">#{sprint.numero}</span>
                      <span className="font-medium">{sprint.nombre}</span>
                      <Badge className={SPRINT_STATUS_COLORS[sprint.estado]}>
                        {SPRINT_STATUS_LABELS[sprint.estado]}
                      </Badge>
                    </div>
                    {sprint.objetivo && (
                      <p className="text-sm text-gray-600 mt-1">{sprint.objetivo}</p>
                    )}
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {sprint.estado === 'PLANIFICADO' && (
                        <DropdownMenuItem onClick={() => handleActivar(sprint)}>
                          <Play className="h-4 w-4 mr-2" />
                          Activar Sprint
                        </DropdownMenuItem>
                      )}
                      {sprint.estado === 'ACTIVO' && (
                        <DropdownMenuItem onClick={() => handleCompletar(sprint)}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Completar Sprint
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleEdit(sprint)}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(sprint)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      {format(parseISO(sprint.fecha_inicio), 'dd MMM', { locale: es })}
                      {' - '}
                      {format(parseISO(sprint.fecha_fin), 'dd MMM', { locale: es })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>
                      {sprint.horas_registradas}h
                      {sprint.capacidad_horas && <> / {sprint.capacidad_horas}h</>}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {sprint.actividades_completadas_count}/{sprint.actividades_count} tareas
                  </div>
                </div>

                {isActive && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Tiempo del sprint</span>
                      <span>{timeProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${timeProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SprintFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        proyectoId={proyectoId}
        sprint={sprintToEdit}
        nextNumero={sprints ? sprints.length + 1 : 1}
      />
    </div>
  );
}
